import { FC } from 'react';
import { Button, Icon } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../../../../Localization';

interface ConferenceNumber {
  phoneNumber: string;
  dialCode?: string;
  label: Record<string, string>;
}

interface ConferenceParticipantsViewProps {
  // Applicant
  applicantCallSid: string;
  applicantLoading: boolean;
  applicantConfStatus: string;
  onAddApplicant: () => void;
  onRemoveApplicant: () => void;

  // Translator
  translatorCallSid: string;
  translatorLoading: boolean;
  translatorConfStatus: string;
  onAddTranslator: () => void;
  onRemoveTranslator: () => void;
  showTranslator: boolean;

  // Conference Numbers
  conferenceNumbers: ConferenceNumber[];
  conferenceNumbersSids: Record<string, string>;
  conferenceNumbersLoading: Record<string, boolean>;
  conferenceNumberStatuses: Record<string, string>;
  onAddConferenceNumber: (phoneNumber: string, dialCode?: string) => void;
  onRemoveConferenceNumber: (phoneNumber: string) => void;
  currentLang: string;
}

/**
 * Component for managing conference call participants
 */
export const ConferenceParticipantsView: FC<ConferenceParticipantsViewProps> = ({
  applicantCallSid,
  applicantLoading,
  applicantConfStatus,
  onAddApplicant,
  onRemoveApplicant,
  translatorCallSid,
  translatorLoading,
  translatorConfStatus,
  onAddTranslator,
  onRemoveTranslator,
  showTranslator,
  conferenceNumbers,
  conferenceNumbersSids,
  conferenceNumbersLoading,
  conferenceNumberStatuses,
  onAddConferenceNumber,
  onRemoveConferenceNumber,
  currentLang
}) => {
  const L = useLocalizedStrings();

  return (
    <>
      <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">{L.applicant.comms.conference_participants}</h3>
      <div className="space-y-2">
        {/* Applicant */}
        {!applicantCallSid ? (
          <Button
            variant="secondary"
            className="w-full"
            onClick={onAddApplicant}
            isDisabled={applicantLoading}
            icon={<Icon name="PhoneArrowUpRightIcon" />}
          >
            {L.applicant.comms.add_applicant}
          </Button>
        ) : (
          <Button
            variant="secondary"
            className="w-full flex items-center justify-center gap-2"
            onClick={onRemoveApplicant}
            isDisabled={applicantLoading}
          >
            <div>{L.applicant.comms.remove_applicant}</div>
          </Button>
        )}

        {/* Translator */}
        {showTranslator && (
          <>
            {!translatorCallSid ? (
              <Button
                variant="secondary"
                className="w-full"
                onClick={onAddTranslator}
                isDisabled={translatorLoading}
                icon={<Icon name="PhoneArrowUpRightIcon" />}
              >
                {L.applicant.comms.add_interpreter}
              </Button>
            ) : (
              <Button
                variant="secondary"
                className="w-full flex items-center justify-center gap-2"
                onClick={onRemoveTranslator}
                isDisabled={translatorLoading}
              >
                <div>{L.applicant.comms.remove_interpreter}</div>
              </Button>
            )}
          </>
        )}

        {/* Conference Numbers */}
        {conferenceNumbers.map((number, index) => {
          const label = number.label[currentLang] || number.label['en'];
          const hasSid = !!conferenceNumbersSids[number.phoneNumber];
          const isLoading = conferenceNumbersLoading[number.phoneNumber];

          return !hasSid ? (
            <Button
              key={index}
              variant="secondary"
              className="w-full"
              onClick={() => onAddConferenceNumber(number.phoneNumber, number.dialCode)}
              isDisabled={isLoading}
              icon={<Icon name="PhoneArrowUpRightIcon" />}
            >
              {L.applicant.comms.add_participant.replace("$participant", label)}
            </Button>
          ) : (
            <Button
              key={index}
              variant="secondary"
              className="w-full flex items-center justify-center gap-2"
              onClick={() => onRemoveConferenceNumber(number.phoneNumber)}
              isDisabled={isLoading}
            >
              {L.applicant.comms.remove_participant.replace("$participant", label)}
            </Button>
          );
        })}
      </div>
    </>
  );
};
